import React, { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { DownloadOutlined } from '@ant-design/icons';
import { message, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/lib/constants';
import { exportSampleDataToCsv, renderCellValue } from '@/shared/lib/format';
import { Button } from '@/shared/ui/Button';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';

import type { SampleDataResponse } from '@/features/asset-detail/api/sampleDataApi';
import type { ColumnSchema } from '@/features/asset-detail/api/schemaApi';

import styles from './SampleDataTab.module.css';

type SampleDataTabProps = {
  sampleData?: SampleDataResponse | null;
  schemaColumns?: ColumnSchema[];
  tableName?: string;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
};

type DataRow = {
  key: number;
  [columnName: string]: unknown;
};

const COLUMN_WIDTHS = {
  SAMPLE_DATA_COLUMN: 180,
} as const;

const SampleDataTabComponent: React.FC<SampleDataTabProps> = ({
  sampleData,
  schemaColumns,
  tableName = 'table',
  isLoading = false,
  isError = false,
  error,
  onRetry,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getColumnDataType = useCallback(
    (columnName: string): string | undefined => {
      const schema = schemaColumns?.find((col) => col.name === columnName);
      return schema?.dataType || schema?.dataTypeDisplay;
    },
    [schemaColumns]
  );

  const columns: ColumnsType<DataRow> = useMemo(() => {
    if (!sampleData?.columns) return [];

    return sampleData.columns.map((columnName) => {
      const dataType = getColumnDataType(columnName);

      return {
        title: (
          <div className={styles.columnHeader}>
            <div className={styles.columnName}>{columnName}</div>
            {dataType && <div className={styles.columnType}>({dataType})</div>}
          </div>
        ),
        dataIndex: columnName,
        key: columnName,
        ellipsis: true,
        width: COLUMN_WIDTHS.SAMPLE_DATA_COLUMN,
        render: (value: unknown) => renderCellValue(value, dataType),
      };
    });
  }, [sampleData?.columns, getColumnDataType]);

  const dataSource: DataRow[] = useMemo(() => {
    if (!sampleData?.rows || !sampleData?.columns) return [];

    return sampleData.rows.map((row, rowIndex) => {
      const rowData: DataRow = { key: rowIndex };
      sampleData.columns.forEach((columnName, colIndex) => {
        rowData[columnName] = row[colIndex];
      });
      return rowData;
    });
  }, [sampleData?.rows, sampleData?.columns]);

  const handleExportCsv = useCallback(() => {
    if (!sampleData?.columns || !sampleData?.rows) {
      message.warning('다운로드할 샘플 데이터가 없습니다.');
      return;
    }

    try {
      exportSampleDataToCsv(sampleData.columns, sampleData.rows, tableName);
      message.success('샘플 데이터가 CSV 파일로 다운로드되었습니다.');
    } catch {
      message.error('CSV 파일 다운로드에 실패했습니다.');
    }
  }, [sampleData, tableName]);

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: dataSource.length,
    viewportOffset: 648,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="샘플 데이터를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.sampleDataTab}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarRight}>
          <Button
            prefix={<DownloadOutlined />}
            onClick={handleExportCsv}
            disabled={!sampleData || isLoading}
            variant="outlined">
            CSV 다운로드
          </Button>
        </div>
      </div>
      <NoDataBoundary data={dataSource} emptyDescription="샘플 데이터가 없습니다.">
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="key"
          loading={isLoading}
          pagination={paginationConfig}
          scroll={{ x: dataSource.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
          locale={{
            emptyText: <div>샘플 데이터가 없습니다.</div>,
          }}
          className={styles.sampleTable}
        />
      </NoDataBoundary>
    </div>
  );
};

export const SampleDataTab = React.memo(SampleDataTabComponent);
